import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './TenantUsers.css';

const formatHasForwarding = (value) => {
  if (value === true || value === 1 || String(value).toLowerCase() === 'true') {
    return 'Yes';
  }
  if (value === false || value === 0 || String(value).toLowerCase() === 'false') {
    return 'No';
  }
  return '—';
};

const formatForwardingRules = (user) => {
  const addresses = Array.isArray(user.forwardingAddresses) ? user.forwardingAddresses : [];
  if (addresses.length > 0) {
    return addresses.join(', ');
  }

  if (user.HasForwardingRules) {
    return 'Configured (no destination addresses listed)';
  }

  if (user.ForwardingRules == null || user.ForwardingRules === '') {
    return '—';
  }

  if (typeof user.ForwardingRules === 'string') {
    return user.ForwardingRules;
  }

  try {
    return JSON.stringify(user.ForwardingRules);
  } catch {
    return '—';
  }
};

const TenantUsers = () => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/tenant-users?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch users');
        setUsers([]);
      } else {
        setUsers(Array.isArray(data.users) ? data.users : []);
      }
    } catch (err) {
      console.error('Error fetching tenant users:', err);
      setError('Network error. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchUsers();
    } else {
      setUsers([]);
      setLoading(false);
    }
  }, [selectedTenant, fetchUsers]);

  const filteredUsers = users.filter((user) => {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    const forwardingText = formatForwardingRules(user).toLowerCase();
    return (
      (user.displayName && user.displayName.toLowerCase().includes(lower)) ||
      (user.userPrincipalName && user.userPrincipalName.toLowerCase().includes(lower)) ||
      forwardingText.includes(lower) ||
      formatHasForwarding(user.HasForwardingRules).toLowerCase().includes(lower)
    );
  });

  if (!selectedTenant) {
    return (
      <div className="tenant-users-container">
        <div className="tenant-users-header">
          <h2>Tenant Users</h2>
        </div>
        <div className="tenant-users-empty">
          <Icon name="user" size="large" className="tenant-users-empty-icon" />
          <p>Please select a tenant to view users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-users-container">
      <div className="tenant-users-header">
        <div>
          <h2>Tenant Users - {selectedTenant.name}</h2>
          <p>Directory users with mail forwarding status and configured rules.</p>
        </div>
        <div className="tenant-users-actions">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tenant-users-search"
          />
          <button className="tenant-users-refresh" onClick={fetchUsers}>
            <Icon name="refresh" size="small" />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="tenant-users-loading">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : error ? (
        <div className="tenant-users-error">⚠️ {error}</div>
      ) : filteredUsers.length === 0 ? (
        <div className="tenant-users-empty">
          <Icon name="user" size="large" className="tenant-users-empty-icon" />
          <h3>No users found</h3>
          <p>Try adjusting the search or refresh the list.</p>
        </div>
      ) : (
        <div className="tenant-users-table-wrapper">
          <table className="tenant-users-table">
            <thead>
              <tr>
                <th>Display Name</th>
                <th>User Principal Name</th>
                <th>Mail Forwarding</th>
                <th>Forwarding Rules</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr key={`${user.userPrincipalName || 'user'}-${index}`}>
                  <td>{user.displayName || '—'}</td>
                  <td>{user.userPrincipalName || '—'}</td>
                  <td>
                    <span
                      className={`tenant-users-forwarding-flag ${
                        user.HasForwardingRules ? 'enabled' : 'disabled'
                      }`}
                    >
                      {formatHasForwarding(user.HasForwardingRules)}
                    </span>
                  </td>
                  <td className="tenant-users-forwarding-rules">
                    {formatForwardingRules(user)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TenantUsers;




